import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * The shared time-range and live/pause state (design 12.2: "time range with a live toggle (pause
 * freezes every chart)").
 *
 * This is a single app-wide state on purpose: when the operator pauses, EVERY chart must freeze, so
 * the charts (built in later tasks) read `live` from this context rather than each owning its own
 * toggle. The shell only provides the state and the control (`TimeRangeControl`); it draws no charts
 * itself. FR-13 AC2: pausing freezes every chart until resumed.
 *
 * The range is expressed as a named preset plus a resolved absolute window. Live mode means the
 * window's end tracks "now"; the resolved window is recomputed by consumers at read time (the shell
 * does not run a clock — a later task that renders a chart decides its own refresh cadence and reads
 * `preset`/`live` here). Pausing snapshots the window so it stops advancing.
 */

/** The relative-range presets the control offers. `custom` carries an explicit absolute window. */
export type RangePreset = "5m" | "15m" | "1h" | "6h" | "24h" | "7d" | "custom";

export interface AbsoluteWindow {
  /** Inclusive start, epoch milliseconds. */
  readonly fromMs: number;
  /** Exclusive end, epoch milliseconds. */
  readonly toMs: number;
}

/** How many milliseconds a relative preset spans. `custom` is not relative, hence absent. */
const PRESET_SPAN_MS: Readonly<Record<Exclude<RangePreset, "custom">, number>> = {
  "5m": 5 * 60_000,
  "15m": 15 * 60_000,
  "1h": 60 * 60_000,
  "6h": 6 * 60 * 60_000,
  "24h": 24 * 60 * 60_000,
  "7d": 7 * 24 * 60 * 60_000,
};

export const RANGE_PRESETS: readonly RangePreset[] = [
  "5m",
  "15m",
  "1h",
  "6h",
  "24h",
  "7d",
  "custom",
];

export interface TimeRangeState {
  /** The selected preset. */
  readonly preset: RangePreset;
  /** True while the view tracks "now"; false when paused (every chart freezes). */
  readonly live: boolean;
  /**
   * The frozen window. In live mode this is `null` and consumers compute the window from `preset`
   * relative to the current time. On pause (or a custom range) it holds the exact window to show, so
   * charts stop advancing. FR-13 AC2.
   */
  readonly frozen: AbsoluteWindow | null;
}

export interface TimeRangeApi extends TimeRangeState {
  /** Choose a preset; selecting a relative preset while paused keeps it paused on the new span. */
  setPreset(preset: RangePreset): void;
  /** Set a custom absolute window (switches the preset to `custom` and pauses). */
  setCustomWindow(window: AbsoluteWindow): void;
  /** Pause: freeze the window at `now` for the current preset. No-op if already paused. */
  pause(nowMs?: number): void;
  /** Resume: track "now" again. No-op if already live. */
  resume(): void;
  /** Toggle live/pause. */
  toggleLive(nowMs?: number): void;
  /**
   * Resolve the absolute window to render for a given "now". In live mode this is `[now - span,
   * now]`; when frozen it is the frozen window. Charts call this; the shell exposes it so a later
   * task does not re-derive the rule.
   */
  resolveWindow(nowMs: number): AbsoluteWindow;
}

function relativeWindow(preset: RangePreset, nowMs: number): AbsoluteWindow {
  // `custom` has no relative span; a sensible fallback (1h) is used only if asked to resolve a
  // custom preset without a frozen window, which the API does not do (custom always freezes).
  const span = preset === "custom" ? PRESET_SPAN_MS["1h"] : PRESET_SPAN_MS[preset];
  return { fromMs: nowMs - span, toMs: nowMs };
}

/**
 * The time-range context. Prefer {@link useTimeRange} (which asserts a provider is present) inside
 * screens; this raw context is exported so reusable, provider-agnostic kit components (e.g. the
 * observability ChartCard) can read it with `useContext` and degrade gracefully to `null` when they
 * are rendered outside a `TimeRangeProvider`, rather than throwing.
 */
export const TimeRangeContext = createContext<TimeRangeApi | null>(null);

export function TimeRangeProvider(props: {
  children: ReactNode;
  /** Initial preset; defaults to a 1-hour live view. */
  initialPreset?: RangePreset;
}): ReactNode {
  const [preset, setPresetState] = useState<RangePreset>(props.initialPreset ?? "1h");
  const [live, setLive] = useState(true);
  const [frozen, setFrozen] = useState<AbsoluteWindow | null>(null);

  const setPreset = useCallback<TimeRangeApi["setPreset"]>(
    (next) => {
      setPresetState(next);
      // Changing to a relative preset while paused re-freezes on the new span at the same end so the
      // operator keeps studying the same incident end-point; going live is a separate action.
      setFrozen((current) => {
        if (next === "custom") return current; // custom is set via setCustomWindow
        if (current === null) return null; // live: nothing to freeze
        const span = PRESET_SPAN_MS[next];
        return { fromMs: current.toMs - span, toMs: current.toMs };
      });
    },
    [],
  );

  const setCustomWindow = useCallback<TimeRangeApi["setCustomWindow"]>((window) => {
    setPresetState("custom");
    setLive(false);
    setFrozen(window);
  }, []);

  const pause = useCallback<TimeRangeApi["pause"]>(
    (nowMs) => {
      setLive((wasLive) => {
        if (!wasLive) return false;
        setFrozen(relativeWindow(preset, nowMs ?? Date.now()));
        return false;
      });
    },
    [preset],
  );

  const resume = useCallback<TimeRangeApi["resume"]>(() => {
    setLive(true);
    setFrozen(null);
    // Resuming from a custom window returns to the last relative preset is not tracked; a custom
    // range resumes as a 1h live view, which is predictable. A later task may remember the prior
    // preset if operators ask for it.
    setPresetState((current) => (current === "custom" ? "1h" : current));
  }, []);

  const toggleLive = useCallback<TimeRangeApi["toggleLive"]>(
    (nowMs) => {
      if (live) pause(nowMs);
      else resume();
    },
    [live, pause, resume],
  );

  const resolveWindow = useCallback<TimeRangeApi["resolveWindow"]>(
    (nowMs) => frozen ?? relativeWindow(preset, nowMs),
    [frozen, preset],
  );

  const value = useMemo<TimeRangeApi>(
    () => ({
      preset,
      live,
      frozen,
      setPreset,
      setCustomWindow,
      pause,
      resume,
      toggleLive,
      resolveWindow,
    }),
    [preset, live, frozen, setPreset, setCustomWindow, pause, resume, toggleLive, resolveWindow],
  );

  return <TimeRangeContext.Provider value={value}>{props.children}</TimeRangeContext.Provider>;
}

export function useTimeRange(): TimeRangeApi {
  const ctx = useContext(TimeRangeContext);
  if (ctx === null) throw new Error("useTimeRange must be used within <TimeRangeProvider>");
  return ctx;
}
